import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useUser } from '../contexts/UserContext'; // Importar el hook del contexto de usuario
import ThemeToggle from './ThemeToggle';
import { applyImageFallback } from '../config/imageUrlHelper';
import { AdminMenuIcon } from './adminMenuIcon';
import FormularioComunidad from './FormularioComunidad';
import styles from './Header.module.css';
import useLockBodyScroll from '../hooks/useLockBodyScroll';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const location = useLocation();
  const { user, loading, logout } = useUser(); // Obtener la información del usuario y la función de logout del contexto
  const navigate = useNavigate(); // Hook para la navegación

  useLockBodyScroll(showFormModal);

  const isLoggedIn = !!user; // El usuario está logueado si hay un objeto de usuario
  const isInPanelRoute = location.pathname.startsWith('/admin');
  const showCommunityFormAccess = !(isLoggedIn && isInPanelRoute);
  const headerItemMotion = {
    initial: { opacity: 0, y: -10, scale: 0.9 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -10, scale: 0.9 },
    transition: { duration: 0.22, ease: 'easeInOut' },
  };

  const toggleMenu = () => {
    if (isMenuOpen) {
      // Al cerrar, activar animación de salida
      setIsAnimating(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsAnimating(false);
      }, 300); // Duración de la animación en ms
    } else {
      // Al abrir, mostrar menú y activar animación de entrada
      setIsMenuOpen(true);
    }
  };

  // Ocultar el enlace de login si estamos en la página de login
  const shouldHideLoginLink = location.pathname === '/login';

  // Manejar logout
  const handleLogout = (e) => {
    e.preventDefault();
    logout(); // Llamar a la función de logout del contexto
    navigate('/'); // Redirigir al inicio después del logout
  };

  return (
    <header data-app-header="true" className={`${styles.header} bg-blue-600 text-white p-4 shadow-md dark:bg-gray-900 dark:text-white sticky top-0 z-40`}>
      <div className={`${styles.headerContainer} mx-auto flex justify-between items-center`}>
        <h1 className={`${styles.headerTitle} text-xl sm:text-2xl font-bold flex items-center gap-2`}>
          <Link to="/">
            <img
              src="/media/images/iconobarba.png"
              alt="Diegoncurso"
              className="h-8"
              onError={e => {
                applyImageFallback(e);
                e.target.alt = "Icono no disponible";
                console.error("No se pudo cargar /media/images/iconobarba.png");
              }}
            />
          </Link>
          {/* Icono menú admin solo si está logueado y NO estás en /admin */}
          <AnimatePresence>
            {isLoggedIn && location.pathname !== "/admin" && (
              <motion.div
                key="admin-panel-icon"
                initial={{ opacity: 0, scale: 0.7, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.7, y: -10 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="inline-block"
              >
                <Link to="/admin" className="ml-2 flex items-center justify-center p-1 rounded-md hover:bg-blue-700 dark:hover:bg-gray-700 transition-colors" title="Panel de administración">
                  <span className="text-white dark:text-blue-300">{AdminMenuIcon}</span>
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </h1>
        {/* Menú para pantallas grandes */}
        <nav className="hidden md:flex items-center">
          <ul className={`${styles.headerNav} flex items-center space-x-4`}>
            <motion.li
              className="flex items-center justify-center"
              animate={{ x: showCommunityFormAccess ? 0 : 14 }}
              transition={{ type: 'spring', stiffness: 320, damping: 26, mass: 0.6 }}
            >
              <ThemeToggle />
            </motion.li>
            <AnimatePresence>
              {showCommunityFormAccess && (
                <motion.li
                  key="desktop-community-form"
                  className="flex items-center justify-center"
                  {...headerItemMotion}
                >
                <button
                  onClick={() => setShowFormModal(true)}
                  className="p-2 rounded-md hover:bg-blue-700 dark:hover:bg-gray-700 transition-colors text-white"
                  title="Enviar pregunta a la comunidad"
                  aria-label="Formulario de la comunidad"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="currentColor" className="w-6 h-6" aria-hidden="true">
                    <path d="M50 6H14a6 6 0 0 0-6 6v40a6 6 0 0 0 6 6h36a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6zm2 46a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h36a2 2 0 0 1 2 2v40z"/>
                    <path d="M20 20h24v4H20zm0 10h24v4H20zm0 10h16v4H20z"/>
                  </svg>
                </button>
                </motion.li>
              )}
            </AnimatePresence>
            {isLoggedIn ? (
              <li className="flex items-center justify-center ml-2">
                <a href="#logout" onClick={handleLogout} className="hover:text-blue-200 transition-colors dark:hover:text-blue-400 py-2 px-3 rounded-md hover:bg-blue-700 dark:hover:bg-gray-700 flex items-center" title="Cerrar sesión">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className="w-6 h-6" aria-hidden="true" role="img">
                    <g transform="translate(0,512) scale(0.1,-0.1)">
                      <path d="M3195 4856 c-16 -7 -43 -25 -58 -40 -54 -50 -57 -71 -57 -369 l0 -274 51 -23 c102 -48 163 -148 163 -265 0 -90 -17 -127 -128 -276 l-86 -117 0 -296 0 -296 239 0 238 0 5 45 c8 65 46 117 109 149 60 29 113 33 165 11 45 -19 598 -448 623 -484 41 -58 48 -148 15 -211 -7 -14 -30 -41 -51 -61 -60 -56 -534 -421 -572 -441-72 -37-182 -16-238 44-27 29-53 89-53 123 l0 25-240 0-240 0 2-863 3-863 24-36 c13-19 40-46 59-59 l36-24 883-3 c854-2 884-2 921 17 47 24 77 56 97 103 13 33 15 269 15 2188 0 1919-2 2155-15 2188-20 47-50 79-97 103-36 18-71 19-910 19-704-1-879-3-903-14z"/>
                      <path d="M1945 4756 c-215 -52 -354 -259 -318 -472 40 -229 257 -386 480 -345 329 60 461 454 233 695-105 111-252 157-395 122z"/>
                      <path d="M2925 4058 c-42 -23-73 -60-256 -305-104 -139-192 -253-197-253-12 0-305 100-312 106-3 3 28 2 68 -2 l74 -7-21 40c-23 46-78 97-125 117-19 8-172 33-341 56-427 59-375 72-855-210-352-206-345-202-370-250-26-51-26-70 9-577 31-446 35-467 88-515 79-71 214-58 279 27 23 31 29 50 32 104 3 58-30 636-43 731l-4 35 122 72c67 40 123 71 125 69 2-2-29-219-68-482-61-416-74-526-96-839-14-198-26-361-27-362-1-1-189-33-417-72-468-80-497-88-549-168-92-139-7-331 155-349 45-5 261 29 984 156 129 22 149 30 199 75 62 57 68 85 91 430 11 171 22 333 26 361l5 51 130-22c72-12 134-26 138-31 4-5 65-130 134-279l126-270 56-530c32-313 61-547 70-571 31-83 119-144 210-144 121 0 224 101 225 220 0 25-27 295-59 600l-59 555-163 345-162 345 48 350c26 193 50 371 53 397l4 46-115 68c-64 37-115 69-114 70 1 2 108-34 237-79 265-92 316-99 382-59 26 16 113 125 278 347 133 178 248 338 257 355 10 22 14 50 11 91-5 67-38 118-98 150-39 21-133 25-165 7z"/>
                      <path d="M3725 2988 c-37 -21-44 -41-45 -125 l0 -83-375 0-376 0-24 -24c-33 -33-37 -64-33 -283l3-193 28-27 27-28 375-3 375-3 0-80c0-72 3-82 25-104 52-53 65-46 371 193 155 120 284 229 291 243 26 54 7 76-225 256-387 301-368 289-417 261z"/>
                    </g>
                  </svg>
                </a>
              </li>
            ) : !shouldHideLoginLink ? (
              <li className="flex items-center justify-center ml-2">
                <Link to="/login" className="hover:text-blue-200 transition-colors dark:hover:text-blue-400 py-2 px-3 rounded-md hover:bg-blue-700 dark:hover:bg-gray-700 flex items-center" title="Login">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className="w-6 h-6" aria-hidden="true" role="img">
                    <g transform="translate(0,512) scale(0.1,-0.1)">
                      <path d="M2116 5104 c-168 -41 -316 -173 -378 -339 -22 -56 -23 -78 -26 -348  -3 -322 -3 -325 65 -353 28 -12 42 -13 69 -4 66 22 68 33 74 335 5 255 6 273 28 320 44 96 133 165 237 185 42 8 317 10 925 8 l865 -3 -355 -119 c-195 -65 -381 -132 -412 -149 -75 -38 -141 -110 -181 -195 l-32 -67 -5 -1760 -5 -1760 -400 0 c-362 0 -405 2 -453 18 -70 25 -152 101 -183 169 -23 51 -24 66 -29 323 -6 302 -8 313 -74 335 -27 9 -41 8 -69 -4 -68 -28 -68 -31 -65 -353 3 -270 4 -292 26 -348 46 -122 140 -231 253 -290 117 -61 148 -65 597 -65 l402 0 0 -145 c0 -120 4 -156 20 -202 29 -84 83 -158 150 -207 84 -62 145 -81 255 -81 l90 1 680 228 c374 125 700 239 725 254 94 54 174 164 199 273 15 65 15 3950 0 4021 -33 153 -158 283 -313 324 -77 21 -2594 19 -2680 -2z m2725 -264 c32 -32 49 -59 57 -92 9 -35 12 -532 12 -1976 0 -1670 -2 -1935 -15 -1978 -15 -51 -59 -106 -104 -128 -14 -7 -321 -111 -681 -232 -730 -243 -721 -241 -807 -188 -34 21 -55 44 -75 83 l-28 53 0 1958 c0 1436 3 1966 11 1991 17 50 55 97 97 118 57 28 1332 450 1372 453 64 6 112 -13 161 -62z"/>
                      <path d="M1350 3833 c-49 -18 -80 -83 -65 -135 4 -12 161 -177 348 -365 l341 -343 -937 0 c-1027 0 -990 2 -1021 -59 -20 -38 -20 -64 0 -102 31 -61 -6 -59 1021 -59 l937 0 -341 -342 c-187 -189-344 -354-348 -366-28 -94 75 -175 157 -124 59 37 896 884 903 915 4 15 4 39 0 55-4 18-148 169-428 451-232 233-440 436-461 452-37 27-72 35-106 22z"/>
                    </g>
                  </svg>
                </Link>
              </li>
            ) : null}
          </ul>
        </nav>
        {/* Botón del menú de hamburguesa para móviles */}
        <div className={`md:hidden flex items-center gap-2 ${isLoggedIn && isInPanelRoute ? 'hidden' : ''}`}>
          <AnimatePresence>
            {showCommunityFormAccess && (
              <motion.button
              key="mobile-community-form"
              {...headerItemMotion}
              onClick={() => setShowFormModal(true)}
              className="text-white focus:outline-none p-2 rounded-md hover:bg-blue-700 dark:hover:bg-gray-700 transition-colors"
              aria-label="Formulario de la comunidad"
              title="Enviar pregunta a la comunidad"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="currentColor" className="w-6 h-6" aria-hidden="true">
                <path d="M50 6H14a6 6 0 0 0-6 6v40a6 6 0 0 0 6 6h36a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6zm2 46a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h36a2 2 0 0 1 2 2v40z"/>
                <path d="M20 20h24v4H20zm0 10h24v4H20zm0 10h16v4H20z"/>
              </svg>
            </motion.button>
            )}
          </AnimatePresence>
          <button 
            onClick={toggleMenu}
            className="text-white focus:outline-none p-2 rounded-md hover:bg-blue-700 dark:hover:bg-gray-700 transition-colors"
            aria-label="Toggle menu"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24" 
              xmlns="http://www.w3.org/2000/svg"
            >
              {isMenuOpen ? (
                <>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </>
              ) : (
                <>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div> {/* Cierre correcto del div principal */}
      {/* Menú lateral para móviles */}
      {(isMenuOpen || isAnimating) && (
        <>
          <div className={`${styles.sideMenuOverlay} ${(isMenuOpen || isAnimating) ? styles.active : ''} fixed inset-0 z-40 bg-black bg-opacity-50`} onClick={isMenuOpen ? toggleMenu : undefined}></div>
          <div className={`${styles.sideMenuContent} ${isMenuOpen ? styles.active : ''} ${isAnimating ? styles.animationOut : styles.animationEnabled} bg-white dark:bg-gray-800`}>
            <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold dark:text-white">Menú</h2>
              <button 
                onClick={toggleMenu}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
                aria-label="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="py-4">
              <ul className="space-y-2">
                {/* Theme Toggle como primera opción */}
                <li className="px-4 py-2 border-b dark:border-gray-700 flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">Tema:</span>
                  <ThemeToggle />
                </li>
                {showCommunityFormAccess && (
                  <li className="px-4 py-2 border-b dark:border-gray-700">
                    <button
                      onClick={() => { setShowFormModal(true); toggleMenu(); }}
                      className="w-full flex items-center gap-2 text-gray-700 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="currentColor" className="w-5 h-5 shrink-0" aria-hidden="true">
                        <path d="M50 6H14a6 6 0 0 0-6 6v40a6 6 0 0 0 6 6h36a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6zm2 46a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h36a2 2 0 0 1 2 2v40z"/>
                        <path d="M20 20h24v4H20zm0 10h24v4H20zm0 10h16v4H20z"/>
                      </svg>
                      Enviar pregunta
                    </button>
                  </li>
                )}
                {isLoggedIn ? (
                  <>
                {user.rol === 'admin' ? (
                  <li><Link to="/admin" className="block px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors w-full text-gray-700 dark:text-gray-100" onClick={toggleMenu}>Admin</Link></li>
                ) : (
                  <li><Link to="/admin" className="block px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors w-full text-gray-700 dark:text-gray-100" onClick={toggleMenu}>Menú</Link></li>
                )}
                <li>
                  <a href="#logout" onClick={(e) => { handleLogout(e); toggleMenu(); }} className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors w-full text-gray-700 dark:text-gray-100 flex items-center" title="Cerrar sesión">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" className="w-6 h-6" aria-hidden="true" role="img">
                      <g transform="translate(0,512) scale(0.1,-0.1)">
                        <path d="M3195 4856 c-16 -7 -43 -25 -58 -40 -54 -50 -57 -71 -57 -369 l0 -274 51 -23 c102 -48 163 -148 163 -265 0 -90 -17 -127 -128 -276 l-86 -117 0 -296 0 -296 239 0 238 0 5 45 c8 65 46 117 109 149 60 29 113 33 165 11 45 -19 598 -448 623 -484 41 -58 48 -148 15 -211 -7 -14 -30 -41 -51 -61 -60 -56 -534 -421 -572 -441 -72 -37 -182 -16 -238 44 -27 29 -53 89 -53 123 l0 25 -240 0 -240 0 2 -863 3 -863 24 -36 c13 -19 40 -46 59 -59 l36 -24 883 -3 c854 -2 884 -2 921 17 47 24 77 56 97 103 13 33 15 269 15 2188 0 1919 -2 2155 -15 2188 -20 47 -50 79 -97 103 -36 18 -71 19 -910 19 -704 -1 -879 -3 -903 -14z"/>
                        <path d="M1945 4756 c-215 -52 -354 -259 -318 -472 40 -229 257 -386 480 -345 329 60 461 454 233 695 -105 111 -252 157 -395 122z"/>
                        <path d="M2925 4058 c-42 -23 -73 -60 -256 -305 -104 -139 -192 -253 -197-253 -12 0 -305 100 -312 106 -3 3 28 2 68 -2 l74 -7 -21 40 c-23 46 -78 97 -125 117 -19 8 -172 33 -341 56 -427 59 -375 72 -855 -210 -352 -206 -345 -202 -370 -250 -26 -51 -26 -70 9 -577 31 -446 35 -467 88 -515 79 -71 214 -58 279 27 23 31 29 50 32 104 3 58 -30 636 -43 731 l-4 35 122 72 c67 40 123 71 125 69 2 -2 -29 -219 -68 -482 -61 -416 -74 -526 -96 -839 -14 -198 -26 -361 -27 -362 -1 -1 -189 -33 -417 -72 -468 -80 -497 -88 -549 -168 -92 -139 -7 -331 155 -349 45 -5 261 29 984 156 129 22 149 30 199 75 62 57 68 85 91 430 11 171 22 333 26 361 l5 51 130 -22 c72 -12 134 -26 138 -31 4 -5 65 -130 134 -279 l126 -270 56 -530 c32 -313 61 -547 70 -571 31 -83 119 -144 210 -144 121 0 224 101 225 220 0 25 -27 295 -59 600 l-59 555 -163 345 -162 345 48 350 c26 193 50 371 53 397 l4 46 -115 68 c-64 37 -115 69 -114 70 1 2 108 -34 237 -79 265 -92 316 -99 382 -59 26 16 113 125 278 347 133 178 248 338 257 355 10 22 14 50 11 91 -5 67 -38 118 -98 150 -39 21 -133 25 -165 7z"/>
                        <path d="M3725 2988 c-37 -21 -44 -41 -45 -125 l0 -83 -375 0 -376 0 -24 -24 c-33 -33 -37 -64 -33 -283 l3 -193 28 -27 27 -28 375 -3 375 -3 0 -80 c0 -72 3 -82 25 -104 52 -53 65 -46 371 193 155 120 284 229 291 243 26 54 7 76 -225 256 -387 301 -368 289 -417 261z"/>
                      </g>
                    </svg>
                  </a>
                </li>
              </>
            ) : !shouldHideLoginLink ? (
              <li><Link to="/login" className="block px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors w-full text-gray-700 dark:text-gray-100" onClick={toggleMenu}>Login</Link></li>
            ) : null}
              </ul>
            </nav>
          </div>
        </>
      )}
      {/* Modal formulario comunidad */}
      {showFormModal && (
        <div
          className="fixed inset-x-0 top-2 bottom-24 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowFormModal(false); }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl h-full max-h-full flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10 gap-3">
              <h2 className="text-lg sm:text-xl font-extrabold dark:text-white">Enviar pregunta a la comunidad</h2>
              <button
                onClick={() => setShowFormModal(false)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors rounded-md p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Cerrar formulario"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4">
              <FormularioComunidad />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
